import { Select } from 'antd';
import { useMemo } from 'react';
import ReorderingIcon from '../../../icons/Reordering.svg?react';
import { ApplicationTargetEnum } from '~types/app-selection';
import { useNavigation } from '~utils/use-navigation';
import { usePreview } from '../context/PreviewContext';
import { useT } from '../context/useT';
import type { PreviewLanguage } from '../i18n/types';
import type { PreviewRegion } from '../theme/tokens';
import style from './style.module.scss';

/**
 * Side panel rendered to the right of the simulated phone in the
 * `/app-preview` page (see Figma node 12404-17237 of the
 * "CT - Conception UI - CMS" file).
 *
 * The panel drives the preview-local `region` and `language` state via
 * `usePreview()` — none of the changes here propagate to the global
 * `applicationSelectionStore`. The "Reorder sections" CTA navigates to
 * the existing Featured reordering page.
 */
export const CustomizePanel = () => {
    const t = useT();
    const navigation = useNavigation();
    const { region, language, setRegion, setLanguage } = usePreview();

    const regionOptions = useMemo(
        () => [
            {
                value: ApplicationTargetEnum.QC,
                label: t.customizePanel.regionQc,
            },
            {
                value: ApplicationTargetEnum.CA,
                label: t.customizePanel.regionCa,
            },
        ],
        [t]
    );

    const languageOptions = useMemo(
        () => [
            { value: 'en', label: t.common.languageEn },
            { value: 'fr', label: t.common.languageFr },
        ],
        [t]
    );

    return (
        <div className={style.panel}>
            <h2 className={style.title}>{t.customizePanel.title}</h2>
            <div className={style.card}>
                <div className={style.fields}>
                    <Select<PreviewRegion>
                        className={style.select}
                        value={region}
                        onChange={setRegion}
                        options={regionOptions}
                        aria-label={t.customizePanel.regionLabel}
                    />
                    <Select<PreviewLanguage>
                        className={style.select}
                        value={language}
                        onChange={setLanguage}
                        options={languageOptions}
                        aria-label={t.customizePanel.languageLabel}
                    />
                </div>
                <div className={style.divider} />
                <button
                    type="button"
                    className={style.reorder_button}
                    onClick={navigation.goToFeaturedReordering}
                >
                    <ReorderingIcon className={style.reorder_icon} />
                    <span>{t.customizePanel.reorderSections}</span>
                </button>
            </div>
        </div>
    );
};
